import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { INFRA } from '../data/farm';
import { BOXES_PER_FLOCK, MAX_FLOCKS, NEST_MAX_CAPACITY, NEST_BASE_CAPACITY } from '../data/balance';
import type { BreedId } from '../data/types';
import { invalidateModifiers } from './modifiers';
import { SAVE_VERSION, createGame } from './state';
import type { GameState } from './types';

/**
 * Save format: JSON of GameState (version 1). Import strings are base64 of
 * the same JSON. `loadState` validates shape, clamps exploit values and runs
 * migrations; it throws SaveError with a readable message on bad input.
 */

export class SaveError extends Error {}

export function serialize(s: GameState): string {
  return JSON.stringify(s);
}

type Migration = (raw: Record<string, unknown>) => void;

/** Numbered migrations: MIGRATIONS[n] upgrades a save from version n to n+1. */
const MIGRATIONS: Record<number, Migration> = {};

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function loadState(json: string): GameState {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new SaveError('That save is not valid JSON.');
  }
  if (!raw || typeof raw !== 'object') throw new SaveError('That save is empty.');
  const r = raw as Record<string, unknown>;
  if (!isNum(r.version)) throw new SaveError('That save has no version — is it from Hen House v0.9? Those saves are not compatible.');
  if (r.version > SAVE_VERSION) throw new SaveError('That save is from a newer version of Hen House.');
  for (let v = r.version; v < SAVE_VERSION; v++) {
    MIGRATIONS[v]?.(r);
    r.version = v + 1;
  }
  if (!Array.isArray(r.flocks) || r.flocks.length === 0) throw new SaveError('That save has no flocks.');
  if (!isNum(r.money) || !isNum(r.lifetimeEarned)) throw new SaveError('That save has a corrupt balance.');

  // Merge onto a fresh game so newly added fields always exist.
  const base = createGame({ seed: isNum(r.seed) ? r.seed : 1, wall: isNum(r.wallAt) ? r.wallAt : Date.now() });
  const s = { ...base, ...r } as GameState;
  s.stats = { ...base.stats, ...(r.stats as object) };
  s.timers = { ...base.timers, ...(r.timers as object) };
  s.buffs = { ...base.buffs, ...(r.buffs as object) };
  s.infra = { ...base.infra, ...(r.infra as object) };
  s.combo = { ...base.combo, ...(r.combo as object) };
  heal(s);
  invalidateModifiers(s);
  return s;
}

/** Clamp out-of-range values so an edited or corrupt save can't break the sim. */
function heal(s: GameState): void {
  s.money = Math.max(0, s.money);
  s.lifetimeEarned = Math.max(s.lifetimeEarned, 0);
  s.flocks = s.flocks.slice(0, MAX_FLOCKS);
  for (const f of s.flocks) {
    f.boxes = (f.boxes ?? []).slice(0, BOXES_PER_FLOCK);
    f.coop ??= [];
    f.crowBuffs ??= [];
    if (f.rooster && !(f.rooster.breed in ROOSTER_BREEDS)) f.rooster = null;
    for (const b of f.boxes) {
      b.capacityLevel = Math.max(0, Math.min(NEST_MAX_CAPACITY - NEST_BASE_CAPACITY, b.capacityLevel | 0));
      b.eggs = Math.max(0, Math.min(NEST_MAX_CAPACITY, b.eggs | 0));
      if (b.hen && !(b.hen.breed in HEN_BREEDS)) b.hen = null;
      if (b.hen) {
        b.hen.traits ??= [];
        if (!isNum(b.hen.progress)) b.hen.progress = 0;
      }
    }
  }
  for (const def of INFRA) s.infra[def.id] = Math.max(0, Math.min(def.maxOwned, s.infra[def.id] | 0));
  s.unlockedBreeds = s.unlockedBreeds.filter((b): b is BreedId => b in HEN_BREEDS);
}

export function exportString(s: GameState): string {
  const bytes = new TextEncoder().encode(serialize(s));
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin);
}

export function importString(str: string): GameState {
  const trimmed = str.trim();
  if (!trimmed) throw new SaveError('Paste a save string first.');
  let json: string;
  try {
    const bin = atob(trimmed);
    json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
  } catch {
    throw new SaveError('That does not look like a Hen House save string.');
  }
  return loadState(json);
}
